import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Landmark, ArrowLeft, Loader2, MapPin, Clock,
  Calendar as CalIcon, Users, DollarSign, CheckCircle2,
} from "lucide-react";
import {
  Select, SelectTrigger, SelectValue, SelectContent,
  SelectItem, SelectGroup, SelectLabel,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BankRunPage() {
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [housings, setHousings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState({ enabled: true, allowed_weekdays: [2, 3], earliest_time: "08:00", latest_time: "12:00" });

  const [form, setForm] = useState({
    full_name: "",
    whatsapp_number: "",
    email: "",
    pickup_location: "",
    dropoff_location: "",
    travel_date: "",
    travel_time: "10:00",
    num_passengers: 1,
    notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [destRes, housingRes, availRes] = await Promise.all([
          axios.get(`${API}/api/destinations`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/housing-units`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/bank-run/availability`).catch(() => ({ data: { enabled: true, allowed_weekdays: [2, 3], earliest_time: "08:00", latest_time: "12:00" } })),
        ]);
        const dests = Array.isArray(destRes.data) ? destRes.data : [];
        const bankList = dests.filter(d => d.is_bank || d.destination_area === "madison_banks");
        setBanks(bankList);
        setHousings(housingRes.data || []);
        setAvailability(availRes.data);
        if (bankList.length === 0) {
          toast.error("Could not load bank list — please refresh");
        }
      } catch {
        toast.error("Failed to load options");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const banksGroupedByArea = {
    "East Towne / E Washington Ave": banks.filter(b => b.category === "bank_east_towne"),
    "State Street / Downtown Madison": banks.filter(b => b.category === "bank_downtown"),
  };

  const submit = async () => {
    if (!form.full_name || !form.whatsapp_number || !form.pickup_location ||
        !form.dropoff_location || !form.travel_date || !form.travel_time) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!availability.enabled) {
      toast.error("Bank Run service is temporarily paused");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/bookings`, {
        ...form,
        trip_type: "bank_run",
        ride_type: "shared",
        airline: "N/A",
        flight_number: "BANK_RUN",
        pay_full_trip: false,
        total_suitcases: 0,
        uses_shipping_company: false,
        payment_method: "cash",
        housing_unit: form.pickup_location,
        housing_address: form.pickup_location,
      });
      toast.success("Bank Run booked! Driver will collect $60 cash on the trip.");
      navigate(`/booking/success?booking_id=${data.booking_id || data.id}&cash=1`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground mb-4 hover:text-blue-700">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
        </Link>

        <Card className="shadow-lg border-blue-100" data-testid="bank-run-card">
          <CardHeader className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Landmark className="w-6 h-6" /> Bank Run Service
            </CardTitle>
            <p className="text-sm text-blue-50 mt-1">
              Round-trip from your Dells housing to a Madison bank — driver waits ~20 min while you handle business.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge className="bg-yellow-400 text-black hover:bg-yellow-400">
                <DollarSign className="w-3 h-3 mr-0.5" />$60 flat (1-4 people)
              </Badge>
              <Badge variant="outline" className="border-blue-200 text-blue-50">
                <Clock className="w-3 h-3 mr-1" />Wait time included
              </Badge>
              <Badge variant="outline" className="border-blue-200 text-blue-50">
                <CheckCircle2 className="w-3 h-3 mr-1" />Round-trip
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="pt-6 space-y-4">
            {!availability.enabled && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 text-center" data-testid="bank-run-paused-banner">
                <p className="font-bold text-red-700">
                  ⏸ Bank Run service is {availability.auto_paused_for ? `paused for ${availability.today_holiday || availability.auto_paused_for}` : "temporarily paused"}
                </p>
                <p className="text-xs text-red-600 mt-1">Please check back soon or WhatsApp dispatch.</p>
              </div>
            )}

            {availability.enabled && availability.next_holiday && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-xs text-yellow-900" data-testid="bank-run-upcoming-holiday">
                ⚠️ <b>Heads up:</b> Bank Runs will be paused on {availability.next_holiday.date} for {availability.next_holiday.name} (banks closed).
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900" data-testid="bank-run-hours-info">
              <p><b>📅 Available days:</b> Any day (banks usually closed Sundays + federal holidays)</p>
              <p><b>🕗 Best window:</b> Banks typically open 9 AM – 5 PM</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="bank-run-name-input"
                  placeholder="As it appears on your ID"
                />
              </div>
              <div>
                <Label>WhatsApp Number *</Label>
                <Input
                  value={form.whatsapp_number}
                  onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                  placeholder="+1 555 123 4567"
                  data-testid="bank-run-whatsapp-input"
                />
              </div>
            </div>

            <div>
              <Label>Email (for receipt)</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="bank-run-email-input"
              />
            </div>

            <div>
              <Label className="flex items-center gap-1"><MapPin className="w-4 h-4 text-blue-600" /> Pickup — Your Dells Housing *</Label>
              <Select
                value={form.pickup_location}
                onValueChange={(v) => setForm({ ...form, pickup_location: v })}
              >
                <SelectTrigger className="w-full h-10" data-testid="bank-run-pickup-select">
                  <SelectValue placeholder="Select your housing…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {housings.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No housing options loaded</div>
                  ) : housings.map(h => (
                    <SelectItem key={h.id || h.name} value={h.name}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {housings.length === 0 && (
                <Input
                  className="mt-2"
                  placeholder="Or type your Dells address"
                  value={form.pickup_location}
                  onChange={(e) => setForm({ ...form, pickup_location: e.target.value })}
                />
              )}
            </div>

            <div>
              <Label className="flex items-center gap-1"><Landmark className="w-4 h-4 text-blue-600" /> Bank Destination *</Label>
              <Select
                value={form.dropoff_location}
                onValueChange={(v) => setForm({ ...form, dropoff_location: v })}
              >
                <SelectTrigger className="w-full h-10" data-testid="bank-run-dropoff-select">
                  <SelectValue placeholder={banks.length === 0 ? "Loading banks…" : "Pick a bank…"} />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {banks.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No banks available — please refresh</div>
                  ) : Object.entries(banksGroupedByArea).map(([area, list]) => (
                    list.length > 0 && (
                      <SelectGroup key={area}>
                        <SelectLabel className="text-xs text-blue-700">{area}</SelectLabel>
                        {list.map(b => (
                          <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    )
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="flex items-center gap-1"><CalIcon className="w-4 h-4" /> Date *</Label>
                <Input
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={form.travel_date}
                  onChange={(e) => setForm({ ...form, travel_date: e.target.value })}
                  data-testid="bank-run-date-input"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Any day</p>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Clock className="w-4 h-4" /> Time *</Label>
                <Input
                  type="time"
                  value={form.travel_time}
                  onChange={(e) => setForm({ ...form, travel_time: e.target.value })}
                  data-testid="bank-run-time-input"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Banks usually open 9 AM</p>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Users className="w-4 h-4" /> # People</Label>
                <Input
                  type="number"
                  min={1}
                  max={4}
                  value={form.num_passengers}
                  onChange={(e) => setForm({ ...form, num_passengers: parseInt(e.target.value) || 1 })}
                  data-testid="bank-run-pax-input"
                />
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Need to deposit a check, then ATM"
                data-testid="bank-run-notes-input"
              />
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-muted-foreground">Cash to Driver</p>
                <p className="text-3xl font-bold text-amber-700" data-testid="bank-run-price">$60.00</p>
                <p className="text-xs text-muted-foreground">Pay on the trip · no card needed</p>
              </div>
              <Button
                onClick={submit}
                disabled={submitting || !availability.enabled}
                className="bg-amber-600 hover:bg-amber-700 h-12 px-6 font-bold"
                data-testid="bank-run-submit-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                {availability.enabled ? "Book — Pay Driver $60" : "Service Paused"}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              💵 Pay your driver $60 cash on the trip · Driver waits ~20 min at the bank · Round-trip from your housing
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
